import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, CheckCircle2, ShieldAlert } from "lucide-react";
import { api, errMsg, fmtDT } from "../lib/api";
import { Btn, Field, Input, Textarea } from "./kit";
import { PhotoSlot, PhotoViewer, AuthImg } from "./Photos";
import Approval from "./Approval";

export const ACTIVE = ["assigned", "en_route", "before_check", "working", "after_check"];
const STATES = [["ok", "OK"], ["issue", "Issue"], ["na", "N/A"], ["inaccessible", "Can't access"]];

function ChecklistItem({ item, value, locked, onChange }) {
  const v = value || {};
  const needReason = v.status === "na" || v.status === "inaccessible";
  return (
    <div className="py-2.5 border-b border-[#1f1f1f] last:border-0" data-testid={`checklist-${item.key}`}>
      <div className="text-sm mb-2">{item.label}</div>
      <div className="grid grid-cols-4 gap-1.5">
        {STATES.map(([k, l]) => (
          <button key={k} type="button" disabled={locked} onClick={() => onChange({ ...v, status: k })} data-testid={`checklist-${item.key}-${k}`}
            className={`h-9 rounded-lg text-xs border transition-colors ${v.status === k ? (k === "ok" ? "border-emerald-600 text-emerald-400 bg-emerald-950/30" : k === "issue" ? "border-red-700 text-red-400 bg-red-950/30" : "border-[#FF6B00] text-[#FF6B00] bg-[#FF6B00]/10") : "border-[#2a2a2a] text-neutral-500"}`}>
            {l}
          </button>
        ))}
      </div>
      {(v.status && v.status !== "ok") || v.note ? (
        <input disabled={locked} value={v.note || ""} onChange={(e) => onChange({ ...v, note: e.target.value })} data-testid={`checklist-${item.key}-note`}
          placeholder={needReason ? "Reason (required) — never take unsafe access" : "Note"}
          className="mt-2 w-full bg-transparent border-b border-[#262626] text-sm py-1.5 outline-none focus:border-[#FF6B00]" />
      ) : null}
    </div>
  );
}

function SlotException({ value, locked, onChange, slotKey }) {
  const [open, setOpen] = useState(!!value);
  if (locked) return value ? <div className="text-xs text-amber-400 mt-1">Not photographed: {value}</div> : null;
  if (!open) return <button type="button" className="text-xs text-neutral-500 mt-1 underline" onClick={() => setOpen(true)} data-testid={`slot-exception-link-${slotKey}`}>Can't photograph safely?</button>;
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Reason this photo isn't possible / not applicable" data-testid={`slot-exception-input-${slotKey}`}
    className="mt-1 w-full bg-transparent border-b border-[#262626] text-xs py-1.5 outline-none focus:border-[#FF6B00]" />;
}

function Corrections({ job, phase, reload }) {
  const [f, setF] = useState({ field: "notes", value: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const list = (job.corrections || []).filter((c) => c.phase === phase);
  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/corrections`, { phase, ...f });
      setF({ field: "notes", value: "", reason: "" });
      toast.success("Correction appended");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2 pt-2" data-testid={`corrections-${phase}`}>
      <div className="text-xs uppercase tracking-wider text-neutral-500">Corrections (appended, originals kept)</div>
      {list.map((c) => (
        <div key={c.id} className="text-sm border-l-2 border-[#FF6B00] pl-3">
          <div><span className="text-neutral-500">{c.field}:</span> {c.value}</div>
          <div className="text-xs text-neutral-500">Reason: {c.reason} · {c.author} · {fmtDT(c.at)}</div>
        </div>
      ))}
      <div className="grid grid-cols-3 gap-2">
        <select value={f.field} onChange={(e) => setF({ ...f, field: e.target.value })} className="bg-[#0F0F0F] border border-[#262626] rounded-lg text-sm px-2 h-10" data-testid={`correction-field-${phase}`}>
          {["notes", "odometer", "warning_lights", "checklist", "completed_work", "unresolved_issues", "new_damage", "photo"].map((x) => <option key={x}>{x}</option>)}
        </select>
        <Input className="col-span-2 h-10" placeholder="Corrected value" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid={`correction-value-${phase}`} />
      </div>
      <Input className="h-10" placeholder="Reason for correction" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid={`correction-reason-${phase}`} />
      <Btn variant="outline" size="sm" onClick={add} loading={busy} disabled={!f.value || !f.reason} data-testid={`correction-add-btn-${phase}`}>Append correction</Btn>
    </div>
  );
}

function PhaseForm({ job, phase, reload, onView }) {
  const c = job.condition?.[phase] || {};
  const locked = c.status === "submitted" || !ACTIVE.includes(job.status);
  const [d, setD] = useState({
    odometer: c.odometer || "", warning_lights: c.warning_lights || "", notes: c.notes || "", checklist: c.checklist || {},
    slot_exceptions: c.slot_exceptions || {}, completed_work: c.completed_work || "", unresolved_issues: c.unresolved_issues || "", new_damage: c.new_damage || "",
  });
  const [busy, setBusy] = useState("");
  const save = async (quiet) => {
    await api.put(`/jobs/${job.id}/condition/${phase}`, d);
    if (!quiet) toast.success("Draft saved");
  };
  const run = async (kind) => {
    setBusy(kind);
    try {
      await save(kind === "submit");
      if (kind === "submit") {
        await api.post(`/jobs/${job.id}/condition/${phase}/submit`);
        toast.success("Report submitted and locked");
      }
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const override = (job.overrides || []).find((o) => o.phase === phase);
  return (
    <div className="space-y-4" data-testid={`phase-form-${phase}`}>
      <div className="flex items-center gap-2 text-sm">
        {c.status === "submitted" ? (
          <span className="flex items-center gap-1.5 text-emerald-400" data-testid={`phase-status-${phase}`}><Lock className="w-4 h-4" />Submitted {fmtDT(c.submitted_at)} by {c.submitted_by}</span>
        ) : (
          <span className="text-neutral-400" data-testid={`phase-status-${phase}`}>Draft {c.updated_at ? `· saved ${fmtDT(c.updated_at)}` : "· not saved yet"}</span>
        )}
      </div>
      {override && <div className="text-xs text-amber-400 flex gap-1.5"><ShieldAlert className="w-4 h-4 shrink-0" />Admin exception: {override.reason} ({override.by})</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {job.slots[phase].map((s) => (
          <div key={s.key}>
            <PhotoSlot jobId={job.id} phase={phase} slot={s.key} label={s.label} required={s.required} photos={job.photos} locked={locked} onUploaded={reload} onView={onView} />
            {s.required && <SlotException slotKey={`${phase}-${s.key}`} value={d.slot_exceptions[s.key]} locked={locked} onChange={(v) => setD({ ...d, slot_exceptions: { ...d.slot_exceptions, [s.key]: v } })} />}
          </div>
        ))}
      </div>
      {phase === "before" ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Odometer"><Input disabled={locked} inputMode="numeric" value={d.odometer} onChange={set("odometer")} data-testid="before-odometer-input" /></Field>
          <Field label="Warning lights"><Input disabled={locked} value={d.warning_lights} onChange={set("warning_lights")} placeholder="None / list" data-testid="before-warning-lights-input" /></Field>
        </div>
      ) : (
        <div className="space-y-3">
          <Field label="Completed work"><Textarea disabled={locked} value={d.completed_work} onChange={set("completed_work")} data-testid="after-completed-work-input" /></Field>
          <Field label="Unresolved issues"><Textarea disabled={locked} value={d.unresolved_issues} onChange={set("unresolved_issues")} data-testid="after-unresolved-input" /></Field>
          <Field label="New damage / incidents"><Textarea disabled={locked} value={d.new_damage} onChange={set("new_damage")} placeholder="None, or describe" data-testid="after-new-damage-input" /></Field>
        </div>
      )}
      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">{job.category === "mechanical" ? "Mechanical" : "Detailing"} checklist</div>
        {job.checklists[phase].map((it) => (
          <ChecklistItem key={it.key} item={it} value={d.checklist[it.key]} locked={locked} onChange={(v) => setD({ ...d, checklist: { ...d.checklist, [it.key]: v } })} />
        ))}
      </div>
      <Field label="Damage / condition notes"><Textarea disabled={locked} value={d.notes} onChange={set("notes")} data-testid={`${phase}-notes-input`} /></Field>
      <p className="text-xs text-neutral-500">This checklist documents the work performed. It does not certify the entire vehicle as safe.</p>
      {!locked && (
        <div className="grid grid-cols-2 gap-2">
          <Btn variant="outline" onClick={() => run("save")} loading={busy === "save"} data-testid={`${phase}-save-draft-btn`}>Save draft</Btn>
          <Btn onClick={() => run("submit")} loading={busy === "submit"} data-testid={`${phase}-submit-btn`}>Submit & lock</Btn>
        </div>
      )}
      {c.status === "submitted" && <Corrections job={job} phase={phase} reload={reload} />}
    </div>
  );
}

function Compare({ job }) {
  const first = (ph, slot) => job.photos.find((p) => p.phase === ph && p.slot === slot);
  const pairs = [["Work area", first("before", "work_area"), first("after", "work_area_after")],
    ["Damage", first("before", "damage"), first("after", "new_damage")], ["Front / result", first("before", "front"), first("after", "finished")]];
  return (
    <div className="space-y-3" data-testid="before-after-compare">
      {pairs.map(([label, b, a]) => (
        <div key={label}>
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">{label}</div>
          <div className="grid grid-cols-2 gap-2">
            {[["Before", b], ["After", a]].map(([t, p]) => (
              <div key={t} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-[#262626]">
                {p ? <AuthImg id={p.id} className="w-full h-full" /> : <div className="w-full h-full flex items-center justify-center text-xs text-neutral-600">No photo</div>}
                <span className="absolute top-1 left-1 text-[10px] font-mono bg-black/70 px-1.5 rounded">{t}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Condition({ job, user, reload, jump }) {
  const [tab, setTab] = useState("before");
  const [view, setView] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!jump) return;
    setTab(jump.tab);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [jump]);
  const tabs = [["before", "Before work"], ["after", "After work"], ["notes", "Notes & approval"]];
  const done = { before: job.gates.before_docs, after: job.gates.after_docs, notes: job.gates.work_authorized };
  return (
    <div ref={ref} className="scroll-mt-20 bg-[#141414] border border-[#262626] rounded-2xl p-4 space-y-4" data-testid="vehicle-condition-section">
      <h3 className="font-head text-lg font-semibold uppercase tracking-wide">Vehicle condition</h3>
      <div className="grid grid-cols-3 gap-1 p-1 bg-[#0F0F0F] rounded-xl border border-[#262626]">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`condition-tab-${k}`}
            className={`h-10 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 ${tab === k ? "bg-[#1f1f1f] text-white" : "text-neutral-500"}`}>
            {done[k] && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}{l}
          </button>
        ))}
      </div>
      {tab === "before" && <PhaseForm key={`b-${job.condition?.before?.status}`} job={job} phase="before" reload={reload} onView={setView} />}
      {tab === "after" && <PhaseForm key={`a-${job.condition?.after?.status}`} job={job} phase="after" reload={reload} onView={setView} />}
      {tab === "notes" && (
        <div className="space-y-5">
          <Approval job={job} user={user} reload={reload} />
          <Compare job={job} />
        </div>
      )}
      <PhotoViewer photo={view} onClose={() => setView(null)} />
    </div>
  );
}
